import {
  Button,
  Card,
  Col,
  Row,
  Spin,
  Typography,
  Form,
  Select,
  Checkbox,
  Progress,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { eOUTPUT_STATUS, ePrivileges, eResultCode } from "../../../utils/enum";
import DateUtils from "../../../utils/date";
import { TScanResult } from "../../../types";
import { TContext, TPrivilege, TResponseModel } from "../../../types/config";
import {
  GetScannedCrateList,
  GetScannedProductList,
  UpdateCrateMapping,
} from "../../../utils/api.constant";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import { UserContext } from "../../../context";
import Utils from "../../../utils";
import { FixedSizeList as List } from "react-window";

type TProps = {
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

function ScannedHistoryForm(props: TProps) {
  const [form] = Form.useForm();
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [prevScannedResult, setPrevScannedResult] = useState<TScanResult[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [crateOptions, setCrateOptions] = useState<any[]>([]);
  const [selectedBlowers, setSelectedBlowers] = useState<number[]>([]);
  const context: TContext = useContext(UserContext);

  useEffect(() => {
    fetchPrevScanData();
    fetchCrateOptions();
  }, []);

  const fetchPrevScanData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };
      const response: TResponseModel = await post(
        GetScannedProductList,
        requestPayload,
      );
      const { dataResponse, data } = response;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        setPrevScannedResult(data);
        setIsLoading(false);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCrateOptions = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response: TResponseModel = await post(
        GetScannedCrateList,
        requestPayload,
      );
      const { dataResponse, data } = response;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        const formattedOptions = data.map((crate: any) => ({
          value: crate.id,
          label: crate.serialNumber,
          data: crate,
        }));
        setCrateOptions(formattedOptions);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle on submit event
  const onFinish = async (values: { crateId: number }) => {
    try {
      const payload = {
        data: {
          id: values.crateId,
          emptyCrate: false, // set true if want to remove the crate against the below scanned products
          scannedProductList: selectedBlowers.map((id) => ({ id })),
        },
      };
      setIsLoading(true);

      const response = await post(UpdateCrateMapping, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        props.onCloseDrawer();
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props.onRefreshList();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // TO RENDER THE EACH ROW IN VIRTUALIZE CONTAINER
  const VirtualRow = ({ index, style, data }: any) => {
    const { items, selectedBlowers, setSelectedBlowers } = data;
    const result = items[index];

    if (!result) return null;

    return (
      <div style={style}>
        <Card bodyStyle={{ padding: 0 }}>
          <div style={{ display: "flex" }}>
            <div
              style={{
                width: "20%",
                backgroundColor:
                  result.outputStatus === eOUTPUT_STATUS.GREEN
                    ? "var(--bs-success)"
                    : "var(--bs-danger)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Checkbox
                checked={selectedBlowers.includes(result.id)}
                onChange={(e) => {
                  const { checked } = e.target;

                  setSelectedBlowers((prev: number[]) =>
                    checked
                      ? [...prev, result.id]
                      : prev.filter((id) => id !== result.id),
                  );
                }}
              />
            </div>

            <div style={{ width: "80%", padding: "1rem" }}>
              <Typography.Text strong>{result.serialNumber}</Typography.Text>
            </div>
          </div>
        </Card>
      </div>
    );
  };

  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish}
        layout="vertical"
        autoComplete="off"
      >
        <FormHeader>
          <Row
            // gutter={16}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Col>
              <h3 style={{ color: "var(--bs-dark)" }}>Unit Scanned History</h3>
            </Col>
            <Col>
              <Button
                type="default"
                loading={isLoading}
                disabled={isLoading}
                onClick={() => {
                  props.onCloseDrawer();
                }}
              >
                Close
              </Button>
            </Col>
          </Row>
        </FormHeader>
        <FormContent>
          <div
            style={{
              transition: "width 0.5s ease",
            }}
          >
            {isLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Spin tip="Loading History..." />
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                    padding: "10px 0",
                    maxHeight: "65vh",
                    overflow: "scroll",
                  }}
                >
                  <List
                    height={700}
                    itemCount={prevScannedResult.length}
                    itemSize={80}
                    width="100%"
                    itemData={{
                      items: prevScannedResult,
                      selectedBlowers,
                      setSelectedBlowers,
                    }}
                  >
                    {VirtualRow}
                  </List>
                </div>
                <Row gutter={16} className="formRowHeight">
                  <Col span={24} style={{ marginTop: "15px" }}>
                    <Form.Item
                      name="crateId"
                      label={`Assign Crate ${selectedBlowers.length === 0 ? "after selecting blowers" : `(${selectedBlowers.length} selected)`}`}
                      rules={[
                        {
                          required: true,
                          message: "Please select a crate!",
                        },
                        {
                          validator: async (_, value) => {
                            if (!value) {
                              return Promise.resolve();
                            }
                            const selectedCrate = crateOptions.find(
                              (c) => c.value === value,
                            );
                            if (selectedCrate) {
                              const availableSlots =
                                selectedCrate.data.crateCapacity -
                                selectedCrate.data.blowerCount;
                              if (selectedBlowers.length > availableSlots) {
                                return Promise.reject(
                                  new Error(
                                    `Not enough space. Available: ${availableSlots}, Selected: ${selectedBlowers.length}`,
                                  ),
                                );
                              }
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Select
                        size="large"
                        disabled={isLoading || selectedBlowers.length === 0}
                        placeholder="Select crate"
                        options={crateOptions}
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        optionRender={(option) => (
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <div style={{ flex: 1 }}>
                              <div>{option.data.label}</div>
                              <Progress
                                percent={
                                  (option.data.data.blowerCount /
                                    option.data.data.crateCapacity) *
                                  100
                                }
                                showInfo={false}
                              />
                            </div>
                            <div style={{ marginLeft: "10px" }}>
                              {`${option.data.data.blowerCount}/${option.data.data.crateCapacity}`}
                            </div>
                          </div>
                        )}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
          </div>
        </FormContent>
        <FormFooter className="formFooter">
          <FormRow className="formRow">
            {Utils.isUserHasAccess(
              context.user?.privilegeList as TPrivilege[],
              ePrivileges.ASSIGNCRATE,
            ) ? (
              <Button
                size="large"
                type="primary"
                onClick={form.submit}
                disabled={isLoading}
                loading={isLoading}
                className="btnDimensions"
              >
                Save
              </Button>
            ) : null}
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}

export default ScannedHistoryForm;
